import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { usePode } from "@/lib/permissoes";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import { useFuncionarios } from "@/hooks/use-equipe";
import {
  useListaPendencias, useFrentesManejo, useCriarItemLista, useCriarItensEmMassa, useEditarOperacao,
  useReordenarItens, useLocaisManejo, useIniciarOperacao, useConcluirOperacao, useCancelarOperacao,
  useReabrirOperacao, useComentarOperacao, useUpdateOperacaoStatus, useOperacaoManejo,
  type GrupoListaView, type ItemListaView, type FrenteManejo,
} from "@/hooks/use-manejo";
import { parsearListaColada, type LinhaColada } from "@shared/lista-pendencias";
import { selosDoItem } from "@shared/manejo-selos";
import { ROTULO_INICIAR, ROTULO_CONCLUIR } from "@shared/manejo-vocabulario";
import { StatusBadge } from "@/components/data/status-badge";
import type { MnjOperacaoWithItens } from "@shared/schema";
import { fetchJson } from "@/lib/queryClient";
import { FolhaConclusao } from "./folha-conclusao";
import { BarraAtualizacao } from "./barra-atualizacao";
import { useFluxoOperador } from "@/hooks/use-fluxo-operador";
import { mensagemDeErro } from "@/lib/erro-humano";
import { NovaOperacaoDialog } from "./nova-operacao-dialog";
import { OperacaoDetalhe } from "./operacao-detalhe";
import { ClipboardList, Check, ChevronDown, ChevronRight, Loader2, MoreHorizontal, Play, Plus, Settings, SlidersHorizontal } from "lucide-react";

// Lista de pendências por frente (migração 079).
//
// A fila de quem manda e de quem faz: uma seção por frente, captura de uma linha
// no topo, ▶ Fazendo e ✓ Feito num toque. Colar a lista do WhatsApp abre uma
// prévia com um checkbox por linha (o riscado vem desmarcado).
//
// Estado de lista na URL (CLAUDE.md §4): só o chip de frente (`frente`) — a guia
// (`guia`) é da página. Dialog, linha em edição e "Feitos" aberto são UI do momento.

const PADRAO_LISTA = { frente: "" };
const TODAS = "";
const SEM_FRENTE = "sem";

export function ListaPendencias({ onConfigurar, onNovaOperacao }: {
  onConfigurar?: () => void;
  /** Abre o diálogo completo de criação, já na frente da seção. */
  onNovaOperacao?: (frenteId: string | null) => void;
}) {
  const { toast } = useToast();
  const { estado, definir } = useEstadoNaUrl(PADRAO_LISTA);
  const podePlanejar = usePode("manejo.planejar");
  const podeOperar = usePode("manejo.operar");
  const podeConfigurar = usePode("manejo.frentes.configurar");

  const { data: lista, isLoading, dataUpdatedAt, isFetching, refetch } = useListaPendencias();
  const fluxo = useFluxoOperador();
  const podeReabrir = usePode("manejo.reabrir");
  const { data: frentes = [] } = useFrentesManejo();
  const criar = useCriarItemLista();
  const emMassa = useCriarItensEmMassa();
  const editar = useEditarOperacao();
  const reordenar = useReordenarItens();
  const cancelar = useCancelarOperacao();
  const reabrir = useReabrirOperacao();
  const comentar = useComentarOperacao();
  const mudarStatus = useUpdateOperacaoStatus();

  // Editar = criar: o MESMO diálogo da criação, em modo editar, com a operação inteira.
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const { data: editandoOp } = useOperacaoManejo(editandoId);
  const [dandoData, setDandoData] = useState<ItemListaView | null>(null);
  const [colado, setColado] = useState<{ frenteId: string; linhas: LinhaColada[] } | null>(null);
  const [concluindo, setConcluindo] = useState<MnjOperacaoWithItens | null>(null);
  const [detalheId, setDetalheId] = useState<string | null>(null);

  const grupos = lista?.grupos ?? [];
  const chave = (g: GrupoListaView) => g.frenteId ?? SEM_FRENTE;
  const visiveis = estado.frente === TODAS ? grupos : grupos.filter(g => chave(g) === estado.frente);
  // "Ocupado" é só das AÇÕES RARAS (cancelar, data, reordenar); Fazendo/Feito têm estado POR ITEM.
  const ocupado = cancelar.isPending || mudarStatus.isPending || reordenar.isPending;

  const acao = async (fn: () => Promise<unknown>, sucesso?: string) => {
    try {
      await fn();
      if (sucesso) toast({ title: sucesso });
    } catch (e) {
      fluxo.erroNaTela(e);
    }
  };

  const handleFeito = async (item: ItemListaView) => {
    // Sem insumo: "Feito" conclui DIRETO (otimista) e o aviso oferece "Desfazer".
    if (!item.comInsumos) { await fluxo.concluir(item.id); return; }
    // Com insumo previsto: a folha de conclusão pede as quantidades realizadas.
    await acao(async () => {
      const op = await fetchJson<MnjOperacaoWithItens>(`/api/manejo/operacoes/${item.id}`);
      setConcluindo(op);
    });
  };

  const confirmarConclusao = async (dados: {
    itens: Array<{ mnjProdutoId: string; qtdRealizada: string }>;
    executores: string[];
    observacao: string | null;
  }) => {
    if (!concluindo) return;
    const ok = await fluxo.concluir(concluindo.id, dados);
    if (ok) setConcluindo(null);
  };

  const handleCancelar = (item: { id: string }) => {
    const motivo = window.prompt("Por que está cancelando? (fica na linha do tempo)");
    if (!motivo?.trim()) return;
    acao(() => cancelar.mutateAsync({ id: item.id, motivo: motivo.trim() }), "Item cancelado");
    setDetalheId(null);
  };

  const mover = (grupo: GrupoListaView, indice: number, delta: -1 | 1) => {
    if (!grupo.frenteId) return;
    const ids = grupo.itens.map(i => i.id);
    const alvo = indice + delta;
    if (alvo < 0 || alvo >= ids.length) return;
    const tmp = ids[indice]; ids[indice] = ids[alvo]; ids[alvo] = tmp;
    acao(() => reordenar.mutateAsync({ frenteId: grupo.frenteId as string, ids }));
  };

  // "Mover todos para…": cada item vai ao fim da fila da frente de destino e ganha o
  // responsável dela se estava sem ninguém (regra do PATCH). Um por vez — a ordem de
  // chegada vira a ordem na fila.
  const moverTodos = async (grupo: GrupoListaView, destinoId: string) => {
    const destino = frentes.find(f => f.id === destinoId);
    if (!destino || grupo.itens.length === 0) return;
    if (!window.confirm(`Mover ${grupo.itens.length} item(ns) para ${destino.nome}?`)) return;
    try {
      for (const item of grupo.itens) {
        await editar.mutateAsync({ id: item.id, frenteId: destinoId });
      }
      toast({ title: `${grupo.itens.length} item(ns) movido(s) para ${destino.nome}` });
    } catch (e: any) {
      toast({ title: "Não deu para mover todos", description: e?.message, variant: "destructive" });
    }
  };

  if (isLoading) return <PageSkeleton variante="cards" linhas={3} />;

  // Sem frente cadastrada e nada solto na lista: o primeiro passo é cadastrar.
  if (grupos.length === 0) {
    return (
      <EmptyState
        icone={ClipboardList}
        titulo="Cadastre as frentes de trabalho"
        descricao={podeConfigurar
          ? "Frentes agrupam as pendências (ex.: Água e Irrigação, Fazenda/Geral). Cadastre a primeira para começar a lista."
          : "Peça a um administrador para cadastrar as frentes de trabalho."}
        acao={podeConfigurar && onConfigurar ? (
          <Button className="min-h-11" onClick={onConfigurar}><Settings className="h-4 w-4 mr-1.5" />Configurar frentes</Button>
        ) : undefined}
        testId="empty-lista"
      />
    );
  }

  return (
    <div className="space-y-4">
      <BarraAtualizacao dataUpdatedAt={dataUpdatedAt} buscando={isFetching} onAtualizar={() => { void refetch(); }} />

      <div className="flex flex-wrap items-center gap-1.5" data-testid="chips-frente">
        <Chip ativo={estado.frente === TODAS} onClick={() => definir({ frente: TODAS })}>Todas</Chip>
        {grupos.map(g => (
          <Chip key={chave(g)} ativo={estado.frente === chave(g)} onClick={() => definir({ frente: chave(g) })}>
            {g.emoji ? `${g.emoji} ` : ""}{g.nome}
            <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums">{g.itens.length}</span>
          </Chip>
        ))}
      </div>

      {visiveis.map(grupo => (
        <SecaoFrente
          key={chave(grupo)}
          grupo={grupo}
          frente={frentes.find(f => f.id === grupo.frenteId) ?? null}
          podePlanejar={podePlanejar}
          podeOperar={podeOperar}
          ocupado={ocupado}
          onCriar={titulo => acao(() => criar.mutateAsync({ titulo, frenteId: grupo.frenteId }))}
          frentesDestino={frentes}
          onMoverTodos={destinoId => moverTodos(grupo, destinoId)}
          onNovaOperacao={onNovaOperacao ? () => onNovaOperacao(grupo.frenteId) : undefined}
          onColar={linhas => grupo.frenteId
            ? setColado({ frenteId: grupo.frenteId, linhas })
            : toast({ title: "Escolha uma frente", description: "Colar vários itens precisa de uma frente.", variant: "destructive" })}
          onIniciar={i => fluxo.iniciar(i.id)}
          onFeito={handleFeito}
          onEditar={i => setEditandoId(i.id)}
          onDarData={setDandoData}
          onTirarData={i => acao(() => mudarStatus.mutateAsync({ id: i.id, status: "backlog" }), "Data removida")}
          onMover={(indice, delta) => mover(grupo, indice, delta)}
          onCancelar={handleCancelar}
          onAbrir={i => setDetalheId(i.id)}
          pendentes={fluxo.pendentes}
          podeReabrir={podeReabrir}
          onReabrir={i => acao(() => reabrir.mutateAsync(i.id), "Item reaberto")}
          onDesfazer={i => fluxo.desfazer(i.id)}
        />
      ))}

      <NovaOperacaoDialog
        open={!!editandoId && !!editandoOp}
        onOpenChange={aberto => !aberto && setEditandoId(null)}
        operacao={editandoOp ?? null}
      />

      <DarDataDialog
        item={dandoData}
        onOpenChange={a => !a && setDandoData(null)}
        onConfirmar={(item, data) => acao(async () => {
          await mudarStatus.mutateAsync({ id: item.id, status: "planejada", dataPrevista: data });
          setDandoData(null);
        }, "Data definida")}
      />

      <PreviaColagem
        colado={colado}
        salvando={emMassa.isPending}
        onOpenChange={a => !a && setColado(null)}
        onCriar={titulos => colado && acao(async () => {
          const r = await emMassa.mutateAsync({ frenteId: colado.frenteId, titulos });
          setColado(null);
          toast({ title: `${r.criadas} ${r.criadas === 1 ? "item criado" : "itens criados"}` });
        })}
      />

      <FolhaConclusao
        operacao={concluindo}
        onOpenChange={a => !a && setConcluindo(null)}
        onConfirmar={confirmarConclusao}
        salvando={!!(concluindo && fluxo.pendentes[concluindo.id])}
      />

      <OperacaoDetalhe
        operacaoId={detalheId}
        onOpenChange={a => !a && setDetalheId(null)}
        onIniciar={op => { void fluxo.iniciar(op.id); setDetalheId(null); }}
        onConcluir={op => { setDetalheId(null); setConcluindo(op); }}
        onReabrir={op => acao(() => reabrir.mutateAsync(op.id), "Operação reaberta")}
        onCancelar={op => handleCancelar(op)}
        onComentar={(op, texto) => acao(() => comentar.mutateAsync({ id: op.id, texto }), "Comentário registrado")}
        onEditar={podePlanejar ? (op => { setDetalheId(null); setEditandoId(op.id); }) : undefined}
        ocupado={ocupado}
      />
    </div>
  );
}

// ─── Seção de uma frente ─────────────────────────────────────────────────────

interface SecaoProps {
  grupo: GrupoListaView;
  frentesDestino: FrenteManejo[];
  onMoverTodos: (destinoId: string) => void;
  onNovaOperacao?: () => void;
  frente: FrenteManejo | null;
  podePlanejar: boolean;
  podeOperar: boolean;
  ocupado: boolean;
  onCriar: (titulo: string) => void;
  onColar: (linhas: LinhaColada[]) => void;
  onIniciar: (i: ItemListaView) => void;
  onFeito: (i: ItemListaView) => void;
  onEditar: (i: ItemListaView) => void;
  onDarData: (i: ItemListaView) => void;
  onTirarData: (i: ItemListaView) => void;
  onMover: (indice: number, delta: -1 | 1) => void;
  onCancelar: (i: ItemListaView) => void;
  onAbrir: (i: ItemListaView) => void;
  /** "Salvando" POR ITEM: um toque lento não trava a tela inteira. */
  pendentes: Record<string, boolean>;
  podeReabrir: boolean;
  /** Coordenação: reabre dentro de 7 dias (`/reabrir`). */
  onReabrir: (i: ItemListaView) => void;
  /** O PRÓPRIO "Feito", nas 2 horas seguintes (`/desfazer`). */
  onDesfazer: (i: ItemListaView) => void;
}

function SecaoFrente(p: SecaoProps) {
  const { grupo } = p;
  const [feitosAbertos, setFeitosAbertos] = useState(false);
  return (
    <section className="space-y-2" data-testid={`secao-${grupo.frenteId ?? SEM_FRENTE}`}>
      <h2 className="text-sm font-semibold">
        {grupo.emoji ? `${grupo.emoji} ` : ""}{grupo.nome}
        <span className="ml-1.5 font-normal text-muted-foreground">· {grupo.itens.length}</span>
        {p.frente?.responsavelNome && (
          <span className="ml-1.5 font-normal text-muted-foreground">· {p.frente.responsavelNome}</span>
        )}
      </h2>

      {p.podePlanejar && grupo.frenteId && <Captura onCriar={p.onCriar} onColar={p.onColar} onCompleto={p.onNovaOperacao} />}

      {/* Itens soltos (criados antes das frentes, ou pelo agente): um toque leva todos
          para uma frente — entram no fim da fila e ganham o responsável dela. */}
      {p.podePlanejar && !grupo.frenteId && grupo.itens.length > 0 && p.frentesDestino.length > 0 && (
        <Select value="" onValueChange={p.onMoverTodos}>
          <SelectTrigger className="min-h-11" data-testid="mover-todos"><SelectValue placeholder="Mover todos para…" /></SelectTrigger>
          <SelectContent>
            {p.frentesDestino.map(f => <SelectItem key={f.id} value={f.id}>{f.emoji ? `${f.emoji} ` : ""}{f.nome}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {grupo.itens.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">Nada pendente nesta frente.</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {grupo.itens.map((item, indice) => (
            <LinhaItem
              key={item.id}
              item={item}
              indice={indice}
              total={grupo.itens.length}
              reordenavel={!!grupo.frenteId}
              {...p}
            />
          ))}
        </ul>
      )}

      {grupo.feitos.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setFeitosAbertos(v => !v)}
            aria-expanded={feitosAbertos}
            className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground"
            data-testid="toggle-feitos"
          >
            {feitosAbertos ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Feitos (7 dias) · {grupo.feitos.length}
          </button>
          {feitosAbertos && (
            <ul className="divide-y rounded-md border">
              {grupo.feitos.map(item => (
                <li key={item.id} className="flex items-center gap-2 px-3 py-2">
                  <button type="button" className="min-w-0 flex-1 text-left" onClick={() => p.onAbrir(item)}>
                    <span className="block text-sm text-muted-foreground line-through line-clamp-2">{item.tituloExibicao}</span>
                  </button>
                  {item.desfazivelPorMim ? (
                    <Button variant="ghost" size="sm" className="min-h-11" disabled={!!p.pendentes[item.id]}
                      onClick={() => p.onDesfazer(item)} data-testid={`btn-desfazer-${item.id}`}>Desfazer</Button>
                  ) : p.podeReabrir ? (
                    <Button variant="ghost" size="sm" className="min-h-11" disabled={!!p.pendentes[item.id]}
                      onClick={() => p.onReabrir(item)}>Reabrir</Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function Captura({ onCriar, onColar, onCompleto }: { onCriar: (t: string) => void; onColar: (l: LinhaColada[]) => void; onCompleto?: () => void }) {
  const [texto, setTexto] = useState("");
  const enviar = () => {
    const t = texto.trim();
    if (!t) return;
    onCriar(t);
    setTexto("");
  };
  return (
    <div className="flex items-center gap-2">
      <Input
        value={texto}
        onChange={e => setTexto(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); enviar(); } }}
        onPaste={e => {
          const colado = e.clipboardData.getData("text");
          // Várias linhas: abre a prévia com checkbox por linha em vez de criar às cegas.
          if (/\r?\n/.test(colado.trim())) {
            e.preventDefault();
            onColar(parsearListaColada(colado));
          }
        }}
        placeholder="Novo item — ou cole a lista"
        className="min-h-11"
        data-testid="input-captura"
      />
      <Button type="button" className="min-h-11 min-w-11 px-0" onClick={enviar} aria-label="Adicionar item" data-testid="btn-captura">
        <Plus className="h-5 w-5" />
      </Button>
      {onCompleto && (
        <Button type="button" variant="outline" className="min-h-11 min-w-11 px-0" onClick={onCompleto}
          aria-label="Criar com mais detalhes" title="Mais detalhes (responsável, data, local, tipo de trabalho)" data-testid="btn-captura-completa">
          <SlidersHorizontal className="h-5 w-5" />
        </Button>
      )}
    </div>
  );
}

function LinhaItem(p: SecaoProps & { item: ItemListaView; indice: number; total: number; reordenavel: boolean }) {
  const { item } = p;
  const fazendo = item.status === "em_andamento";
  const salvando = !!p.pendentes[item.id];
  // H: o responsável já está no cabeçalho do grupo — só aparece na linha quando é
  // OUTRO (ou mais de um).
  const doGrupo = p.frente?.responsavelFuncionarioId ?? null;
  const diferenteDoGrupo = item.atribuidos.length > 1
    || (item.atribuidos.length === 1 && item.atribuidos[0].funcionarioId !== doGrupo);
  const responsaveis = diferenteDoGrupo ? item.atribuidos.map(a => a.nome).join(", ") : "";
  const selos = selosDoItem(item, hojeNaFazenda());

  return (
    // < 480 px: DUAS linhas (título em cima, ações embaixo, 8 px entre os botões);
    // a partir de 480 px, uma linha só.
    <li
      className={cn("space-y-2 px-3 py-3 min-[480px]:flex min-[480px]:items-center min-[480px]:gap-2 min-[480px]:space-y-0 min-[480px]:py-2", fazendo && "bg-muted/40")}
      data-testid={`item-${item.id}`}
    >
      <button type="button" className="block min-w-0 w-full text-left min-[480px]:flex-1" onClick={() => p.onAbrir(item)}>
        <span className="block text-base font-medium leading-snug line-clamp-3 sm:text-sm sm:line-clamp-2">{item.tituloExibicao}</span>
        {(item.local || responsaveis || selos.length > 0) && (
          <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground sm:text-xs">
            {selos.map(sl => <StatusBadge key={sl.chave} tom={sl.tom}>{sl.texto}</StatusBadge>)}
            {item.local && <span>{item.local}</span>}
            {responsaveis && <span>{responsaveis}</span>}
          </span>
        )}
      </button>
      <div className="flex items-center gap-2 min-[480px]:shrink-0">
        {p.podeOperar && !fazendo && (
          <Button className="min-h-11 flex-1 px-3 min-[480px]:flex-none" disabled={salvando}
            onClick={() => p.onIniciar(item)} data-testid={`btn-fazendo-${item.id}`}>
            {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}<span className="ml-1.5">{ROTULO_INICIAR}</span>
          </Button>
        )}
        {p.podeOperar && (
          <Button variant={fazendo ? "default" : "outline"} className="min-h-11 flex-1 px-3 min-[480px]:flex-none" disabled={salvando}
            onClick={() => p.onFeito(item)} data-testid={`btn-feito-${item.id}`}>
            {salvando && fazendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}<span className="ml-1.5">{ROTULO_CONCLUIR}</span>
          </Button>
        )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="min-h-11 min-w-11 px-0" aria-label="Mais ações"><MoreHorizontal className="h-5 w-5" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => p.onAbrir(item)}>Abrir detalhe</DropdownMenuItem>
          {p.podeOperar && item.status === "backlog" && <DropdownMenuItem onClick={() => p.onDarData(item)}>Dar data</DropdownMenuItem>}
          {p.podeOperar && item.status === "planejada" && (
            <>
              <DropdownMenuItem onClick={() => p.onDarData(item)}>Mudar data</DropdownMenuItem>
              <DropdownMenuItem onClick={() => p.onTirarData(item)}>Tirar data</DropdownMenuItem>
            </>
          )}
          {p.podePlanejar && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => p.onEditar(item)}>Editar</DropdownMenuItem>
              {p.reordenavel && p.indice > 0 && <DropdownMenuItem onClick={() => p.onMover(p.indice, -1)}>Subir</DropdownMenuItem>}
              {p.reordenavel && p.indice < p.total - 1 && <DropdownMenuItem onClick={() => p.onMover(p.indice, 1)}>Descer</DropdownMenuItem>}
              <DropdownMenuItem className="text-erro" onClick={() => p.onCancelar(item)}>Cancelar item</DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </li>
  );
}

// ─── Diálogos ────────────────────────────────────────────────────────────────

function PreviaColagem({ colado, salvando, onOpenChange, onCriar }: {
  colado: { frenteId: string; linhas: LinhaColada[] } | null;
  salvando: boolean;
  onOpenChange: (aberto: boolean) => void;
  onCriar: (titulos: string[]) => void;
}) {
  // Marcações editadas pelo usuário, por índice; sem entrada = a sugestão do parser.
  const [alteradas, setAlteradas] = useState<Record<number, boolean>>({});
  const linhas = colado?.linhas ?? [];
  const marcada = (i: number) => (i in alteradas ? alteradas[i] : linhas[i].marcada);
  const escolhidas = linhas.filter((_, i) => marcada(i)).map(l => l.titulo);

  return (
    <Dialog open={!!colado} onOpenChange={a => { if (!a) setAlteradas({}); onOpenChange(a); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Criar itens da lista colada</DialogTitle>
          <DialogDescription>Desmarque o que não é item. O que estava riscado já veio desmarcado.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1" data-testid="previa-colagem">
          {linhas.map((l, i) => (
            <li key={i}>
              <label className="flex min-h-11 cursor-pointer items-start gap-3 py-2">
                <Checkbox checked={marcada(i)} onCheckedChange={v => setAlteradas(a => ({ ...a, [i]: v === true }))} className="mt-0.5" />
                <span className={cn("text-sm", !marcada(i) && "text-muted-foreground line-through")}>{l.titulo}</span>
              </label>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" className="min-h-11" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="min-h-11" disabled={escolhidas.length === 0 || salvando} onClick={() => onCriar(escolhidas)} data-testid="btn-criar-colados">
            Criar {escolhidas.length} {escolhidas.length === 1 ? "item" : "itens"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DarDataDialog({ item, onOpenChange, onConfirmar }: {
  item: ItemListaView | null;
  onOpenChange: (aberto: boolean) => void;
  onConfirmar: (item: ItemListaView, data: string) => void;
}) {
  const [data, setData] = useState(hojeNaFazenda());
  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Data do item</DialogTitle>
          <DialogDescription>{item?.tituloExibicao}</DialogDescription>
        </DialogHeader>
        <Input type="date" value={data} onChange={e => setData(e.target.value)} className="min-h-11" />
        <DialogFooter>
          <Button variant="outline" className="min-h-11" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="min-h-11" disabled={!data} onClick={() => item && onConfirmar(item, data)}>Confirmar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Chip({ ativo, onClick, children }: { ativo: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ativo}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 min-h-11 text-sm transition-colors",
        ativo ? "border-primary bg-primary/10 font-semibold" : "text-muted-foreground hover:bg-muted/40",
      )}
    >
      {children}
    </button>
  );
}
